import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { List, Grid, Stack, Box } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import {
  ITemplateDTO,
  RtrTemplateDTO,
  createAllRtrTemplate,
  updateAllRtrTemplate,
} from 'wisestep-api';
import {
  WsColors,
  ScrollableTabs,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsButton,
  WsEditor,
  ControlledTextInput,
  Item,
  useWsEditor,
  TagsInput,
} from 'wisestep-base';
import { object, string } from 'yup';

import DefaultSubmissionTemplateCustomisationTable from '../../../../components/DefaultSubmissionTemplateCustomisationTable';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import { rtrAmplitudeEvents } from '../../../../features/conversationWidget/utils/rtrHelper';
import {
  defaultSubmissionTableData,
  getTableHead,
} from '../../../../features/submissions/submissionListing/helper/shareCandidateProfileEmailHelper';
import { MULTIPLE_SUBMISSION_TEMPLATES } from '../../../../features/templateSettings/helper/constants';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { useAssignedTeamsTableSelectedRows } from '../../../jobs/utils/useTablePreSelectedRows';

import CandidatePlaceholder from './CandidatePlaceholder';
import JobPlaceholder from './JobPlaceholder';
import RecruiterPlaceholder from './RecruiterPlaceholder';
import RtrPlaceholder from './RtrPlaceholder';

const StyledList = styled(List)({
  marginLeft: 15,
  maxHeight: '70vh',
  overflow: 'auto',
  '&& .Mui-selected, && .Mui-selected:hover': {
    backgroundColor: 'red',
    '&, & .MuiListItemIcon-root': {
      color: 'pink',
    },
  },
  '& .MuiListItem-root:hover': {
    backgroundColor: WsColors.TheBlues50,
    '&, & .MuiListItemIcon-root': {
      color: WsColors.TheBlues500,
    },
  },
});
const StyledFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: '10px',
  textAlign: 'center',
  right: '5px',
  width: '29vw',
  justifyContent: 'flex-end',
  backgroundColor: WsColors.White,
}));
const StyledBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: 1,
  margin: 1,
  borderRadius: 1,
  bottom: 10,
}));
export interface CustomTemplateEditorProps {
  onSaveTemplate(formData: ITemplateDTO);
  variant?: 'new' | 'edit' | 'clone';
  onEditOrCloneTemplate?: ITemplateDTO;
  type?: string;
  isRTRTemplate?: boolean;
  isRTRTemplateEdit?: boolean;
  dataRTRTemplate?: RtrTemplateDTO;
}
const EmailTextBorder = styled(Item)(({ theme }) => ({
  borderBottom: '1px solid ' + WsColors.TertiaryShades100,
  borderRadius: 0,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
}));
const CreateTemplateEditor: FC<CustomTemplateEditorProps> = ({
  onSaveTemplate,
  onEditOrCloneTemplate,
  variant,
  type,
  isRTRTemplate,
  isRTRTemplateEdit,
  dataRTRTemplate,
}) => {
  const updateAssignedTeamsRows = useAssignedTeamsTableSelectedRows(
    (state) => state.updatePreSelectedRows
  );
  const [bodyContent, setBodyContent] = useState<string | undefined>('THis is demo for string bdy');
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspace = useWorkspaceContext();

  const TemplateDefaultValues = {
    title: variant === 'new' ? '' : onEditOrCloneTemplate?.name,
    cc: variant === 'new' ? [] : onEditOrCloneTemplate?.cc,
    bcc: variant === 'new' ? [] : onEditOrCloneTemplate?.bcc,
    subject: variant === 'new' ? '' : onEditOrCloneTemplate?.subject,
    body: isRTRTemplateEdit
      ? dataRTRTemplate?.body?.body
      : variant === 'new'
      ? type == MULTIPLE_SUBMISSION_TEMPLATES
        ? getTableHead(defaultSubmissionTableData)
        : ''
      : type == MULTIPLE_SUBMISSION_TEMPLATES
      ? onEditOrCloneTemplate?.body?.slice(0, onEditOrCloneTemplate?.body?.indexOf('{{')) +
        getTableHead(JSON.parse(onEditOrCloneTemplate?.submissionFields ?? '')) +
        onEditOrCloneTemplate?.body?.slice(onEditOrCloneTemplate?.body?.indexOf('</table>'))
      : onEditOrCloneTemplate?.body,
    submissionFields: variant === 'new' ? '' : onEditOrCloneTemplate?.submissionFields ?? '',
  };
  if (bodyContent === '' && TemplateDefaultValues?.body != '') {
    setBodyContent(TemplateDefaultValues?.body);
  }
  const validationSchema = object().shape({
    body: string().required('The body cannot be empty'),
    title: string().required('The title cannot be empty'),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: TemplateDefaultValues,
  };
  const methods = useForm(formOptions);
  const { handleSubmit, setValue, reset, watch, getValues } = methods;
  const onFormDiscard = () => {
    editor?.commands.setContent('');
    setValue('subject', ``);
    reset();
  };

  const addRTREmailTemplate = async (data: RtrTemplateDTO) => {
    try {
      await createAllRtrTemplate(data);
      rtrAmplitudeEvents('rtr_created', 'RTR template created', workspace);
      setNotification(success(`${data.templateName} has been created successfully !!`));
    } catch (error) {
      setNotification(warning(`Oops , Something went wrong`));
      console.error('Error fetching information categories:', error);
    }
  };

  const editRTREmailTemplate = async (data: RtrTemplateDTO) => {
    try {
      await updateAllRtrTemplate(data.id as string, data);
      rtrAmplitudeEvents('rtr_edited', 'RTR template edited', workspace);
      setNotification(success(`${data.templateName} has been updated successfully !!`));
    } catch (error) {
      setNotification(warning(`Oops , Something went wrong`));
      console.error('Error fetching information categories:', error);
    }
  };

  const onFormSubmitSave = async (data: FieldValues) => {
    if (isRTRTemplate) {
      if (isRTRTemplateEdit) {
        const formData: RtrTemplateDTO = {
          id: dataRTRTemplate?.id,
          templateName: data.title,
          body: { body: data.body },
        };
        editRTREmailTemplate(formData);
      } else {
        const formData: RtrTemplateDTO = {
          templateName: data.title,
          body: { body: data.body },
        };
        addRTREmailTemplate(formData);
      }
    } else {
      const formData: ITemplateDTO = {
        bcc: data.bcc ?? [],
        cc: data.cc ?? [],
        id: variant === 'edit' ? onEditOrCloneTemplate?.id : '',
        name: data.title,
        templateCode: data.title,
        subject: data.subject,
        body: data.body,
        submissionFields: data.submissionFields,
      };
      if (!data.bcc || !data.cc || !data.subject) {
        setNotification(warning(`Oops , Please check CC, BCC and Subject are mandotory filed`));
      } else {
        updateAssignedTeamsRows([]);
        onSaveTemplate(formData);
      }
    }
  };
  useEffect(() => {
    if (isRTRTemplateEdit && dataRTRTemplate) {
      methods.setValue('title', dataRTRTemplate.templateName);
    }
  }, [dataRTRTemplate, isRTRTemplateEdit, methods]);

  const formLabel = (label: string) => (
    <Grid xs={2} item>
      <Stack direction={'row'} justifyContent={'space-between'} pr={2}>
        <WsTypography color={WsTypoColors.SENARY} variant={WsTypoVariants.BODY1}>
          {label}
        </WsTypography>
        <WsTypography color={WsTypoColors.SENARY} variant={WsTypoVariants.BODY1}>
          :
        </WsTypography>
      </Stack>
    </Grid>
  );
  const EmailInputField = styled(TagsInput)(() => ({
    '.MuiOutlinedInput-root': {
      fieldset: {
        border: 'none',
      },
      padding: 0,
      margin: 0,
    },
    ':inputProps': {
      padding: 0,
    },
  }));
  const CCBCCInput = (name: 'cc' | 'bcc') => (
    <Grid xs={7} item>
      <EmailInputField
        name={name}
        onTagsChange={
          name === 'cc'
            ? (emails: string[]) => setValue('cc', emails)
            : (emails: string[]) => setValue('bcc', emails)
        }
        initialValue={
          name === 'cc' ? TemplateDefaultValues.cc ?? [] : TemplateDefaultValues.bcc ?? []
        }
        multiple={true}
      />
    </Grid>
  );
  const TitleInput = () => (
    <Grid xs={10} item>
      <ControlledTextInput
        name="title"
        id="title"
        type="text"
        fullWidth
        variant="standard"
        inputProps={{
          style: {
            padding: 0,
          },
        }}
        InputProps={{
          disableUnderline: true,
        }}
      />
    </Grid>
  );

  const SubjectInput = () => (
    <Grid xs={10}>
      <ControlledTextInput
        name="subject"
        type="text"
        fullWidth
        variant="standard"
        onFocus={() => setCurrentEditor('subject')}
        inputProps={{
          style: {
            padding: 0,
          },
        }}
        InputProps={{
          disableUnderline: true,
        }}
      />
    </Grid>
  );

  const [tabPanelPage, setTabPanelPage] = useState(0);
  const [currentEditor, setCurrentEditor] = useState<string>('subject');

  const editor = useWsEditor(
    TemplateDefaultValues.body
      ? '<p><br class="ProseMirror-trailingBreak"></p>' +
          TemplateDefaultValues.body +
          '</table><p><br class="ProseMirror-trailingBreak"></p>' ?? ''
      : '',
    (htmlBody) => {
      setValue('body', htmlBody);
      htmlBody && setBodyContent(htmlBody);
    },
    () => {},
    () => setCurrentEditor('body')
  );

  const placeholderAction = (placeholder: string) => {
    if (currentEditor === 'subject') {
      const subjectValue = watch('subject');
      setValue('subject', `${subjectValue} ${placeholder}`);
    } else {
      editor?.commands.insertContent(placeholder);
    }
  };
  const [showBcc, setShowBcc] = useState<boolean>(false);

  const scrollList = [
    {
      tabName: 'Candidate',
    },
    {
      tabName: 'Job',
    },
    {
      tabName: 'Recruiter',
    },
  ];

  if (isRTRTemplate) {
    scrollList.push({
      tabName: 'RTR',
    });
  }

  const onApply = (selectedValues: Record<string, unknown>[]) => {
    const bodyHeader = bodyContent?.slice(0, bodyContent?.indexOf('<table'));
    const bodyFooter = bodyContent?.slice(bodyContent?.indexOf('</table>'));
    const uniqueMap = new Map();

    const unique = selectedValues.filter((el) => {
      const val = uniqueMap.get(el.value);

      uniqueMap.set(el.value, el.parameter);
      return val ? el.parameter != val : true;
    });

    editor?.commands.deleteRange({ from: 0, to: editor.state.doc.content.size });
    editor?.commands.insertContent(bodyHeader + getTableHead(unique) + bodyFooter);

    setValue('submissionFields', JSON.stringify(unique));
  };

  return (
    <Grid spacing={2} marginTop={2} marginLeft={2} container bgcolor={WsColors.TertiaryShades50}>
      <Grid item xs={3}>
        <ScrollableTabs
          scrollList={scrollList}
          getTabIndex={(index: number) => setTabPanelPage(index)}
          defaultValue={tabPanelPage}
        ></ScrollableTabs>
        <StyledList>
          {tabPanelPage === 0 && type != MULTIPLE_SUBMISSION_TEMPLATES && (
            <CandidatePlaceholder onClickPlaceholder={placeholderAction} />
          )}
          {tabPanelPage === 1 && <JobPlaceholder onClickPlaceholder={placeholderAction} />}
          {tabPanelPage === 2 && <RecruiterPlaceholder onClickPlaceholder={placeholderAction} />}
          {tabPanelPage === 3 && <RtrPlaceholder onClickPlaceholder={placeholderAction} />}
          {tabPanelPage === 0 && type == MULTIPLE_SUBMISSION_TEMPLATES && (
            <DefaultSubmissionTemplateCustomisationTable
              onSelectValues={onApply}
              type="template"
              defaultSelectedValues={
                getValues('submissionFields')
                  ? JSON.parse(getValues('submissionFields'))
                  : defaultSubmissionTableData
              }
            />
          )}
        </StyledList>
      </Grid>
      <Grid bgcolor={WsColors.White} item xs={9}>
        <FormProvider {...methods}>
          <form>
            <EmailTextBorder>
              <Stack direction="row" alignItems={'center'}>
                {formLabel('Title')}
                <TitleInput />
              </Stack>
            </EmailTextBorder>
            {!isRTRTemplate && (
              <>
                <EmailTextBorder>
                  <Stack direction="row" alignItems={'center'}>
                    {formLabel('Subject')}
                    <SubjectInput />
                  </Stack>
                </EmailTextBorder>

                <Grid container direction="row" justifyContent="space-between" display="flex">
                  <Grid item xs={8}>
                    <EmailTextBorder>
                      <Stack direction="row" alignItems={'center'}>
                        {formLabel('CC')}
                        {CCBCCInput('cc')}
                      </Stack>
                    </EmailTextBorder>
                  </Grid>
                  <Grid item xs={2} pt={2} direction="row-reverse" justifyContent="flex-end">
                    <WsButton onClick={() => setShowBcc(!showBcc)} variant="text">
                      {showBcc ? 'Hide BCC' : 'Show BCC'}
                    </WsButton>
                  </Grid>
                </Grid>

                {showBcc && (
                  <EmailTextBorder>
                    <Stack direction="row" alignItems={'center'}>
                      {formLabel('BCC')}
                      {CCBCCInput('bcc')}
                    </Stack>
                  </EmailTextBorder>
                )}
              </>
            )}
            <WsEditor key="body" editor={editor} resizable={true} />
            <StyledFooter>
              <StyledBox>
                <WsButton onClick={onFormDiscard} variant="text" size="large">
                  Discard
                </WsButton>
                <WsButton onClick={handleSubmit(onFormSubmitSave)} variant="contained" size="large">
                  Save Template
                </WsButton>
              </StyledBox>
            </StyledFooter>
          </form>
        </FormProvider>
      </Grid>
    </Grid>
  );
};
export default CreateTemplateEditor;
